/**
 * ConfirmDialog — wiederverwendbarer Bestätigungsdialog für destruktive Aktionen.
 *
 * Ein schlanker MUI-`Dialog` mit Warntext, einem "Abbrechen"- und einem
 * destruktiven "Löschen"-Button (`color="error"`). Alle Texte laufen über das
 * vorhandene i18n (`t(...)`). Während die zugehörige Aktion läuft (`pending`),
 * wird der Bestätigen-Button deaktiviert und zeigt einen Busy-Zustand; der
 * Dialog lässt sich dann nicht schließen.
 *
 * Verwendet für Gruppe löschen (GroupSettings) und Wochenplan löschen
 * (Wochenplan-Ansicht). Rein präsentational: der Aufrufer hält den Zustand
 * (open) und reicht die Mutations-Handler über onConfirm/onCancel ein.
 */

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useI18n } from "../i18n/I18nProvider";

export interface ConfirmDialogProps {
  /** Ob der Dialog sichtbar ist. */
  open: boolean;
  /** Titel-Text (bereits übersetzt). */
  title: string;
  /** Warn-/Beschreibungstext (bereits übersetzt). */
  message: string;
  /**
   * Optionaler übersetzter Text für den Bestätigen-Button.
   * Standard: `t("common.delete")` ("Löschen").
   */
  confirmLabel?: string;
  /**
   * Optionaler übersetzter Text für eine dritte „Ablehnen"-Aktion. Ist er
   * gesetzt, erscheint zwischen „Abbrechen" und „Bestätigen" ein zusätzlicher
   * Button, der `onDecline` auslöst. Damit lassen sich drei Ausgänge
   * unterscheiden (Abbrechen/Bleiben, Ablehnen/Fortfahren, Bestätigen) — z. B.
   * für den „Benachrichtigen beim Verlassen"-Prompt. Ohne `declineLabel`
   * verhält sich der Dialog wie bisher (nur zwei Buttons).
   */
  declineLabel?: string;
  /** Läuft die bestätigte Aktion gerade? Deaktiviert die Buttons + Busy-State. */
  pending?: boolean;
  /** Abbrechen / Schließen ohne Aktion (Cancel-Button + Backdrop). */
  onCancel: () => void;
  /** Bestätigen — löst die (ggf. destruktive) Aktion aus. */
  onConfirm: () => void;
  /** Ablehnen — nur relevant, wenn `declineLabel` gesetzt ist. */
  onDecline?: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  declineLabel,
  pending = false,
  onCancel,
  onConfirm,
  onDecline,
}: ConfirmDialogProps) {
  const { t } = useI18n();

  return (
    <Dialog
      open={open}
      onClose={() => {
        // Während die Aktion läuft, darf der Dialog nicht weggeklickt werden.
        if (!pending) onCancel();
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        "aria-label": title,
        "data-testid": "confirm-dialog",
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={pending}
          sx={{ minHeight: 44 }}
        >
          {t("common.cancel")}
        </Button>
        {declineLabel != null && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onDecline}
            disabled={pending}
            sx={{ minHeight: 44 }}
          >
            {declineLabel}
          </Button>
        )}
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={pending}
          aria-busy={pending}
          startIcon={
            pending ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          sx={{ minHeight: 44 }}
        >
          {confirmLabel ?? t("common.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
